import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHeader } from "@/components/common/Widgets";
import ContextualUploader from "@/components/common/ContextualUploader";
import { fmtDate } from "@/lib/format";
import { MapPin, Camera, Loader2, CheckCircle2 } from "lucide-react";

export default function FieldVerificationSection({ app, fvList, reload }) {
  const [form, setForm] = useState({
    location: { lat: "", lng: "", accuracy_m: "" },
    address_observed: "",
    checklist: { business_active: true, signboard_visible: true, stock_observed: true, machinery_observed: false, employees_count: 0, neighbor_feedback: "Positive", market_reputation: "Good", activity_matches: true },
    notes: "",
    recommendation: "Recommended",
  });
  const [capturing, setCapturing] = useState(false);
  const [busy, setBusy] = useState(false);

  const setL = (k, v) => setForm((f) => ({ ...f, location: { ...f.location, [k]: v } }));
  const setC = (k, v) => setForm((f) => ({ ...f, checklist: { ...f.checklist, [k]: v } }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const captureLocation = () => {
    if (!navigator.geolocation) {
      // Fallback mock
      setL("lat", 27.103); setL("lng", 93.696); setL("accuracy_m", 15);
      toast.message("Geolocation not available — using simulated coords for Naharlagun");
      return;
    }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition((pos) => {
      setL("lat", pos.coords.latitude.toFixed(6));
      setL("lng", pos.coords.longitude.toFixed(6));
      setL("accuracy_m", Math.round(pos.coords.accuracy));
      toast.success("Location captured");
      setCapturing(false);
    }, (err) => {
      // Fallback simulated
      setL("lat", 27.103); setL("lng", 93.696); setL("accuracy_m", 15);
      toast.message("GPS denied — using simulated coords for Naharlagun");
      setCapturing(false);
    }, { enableHighAccuracy: true, timeout: 8000 });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.location.lat || !form.location.lng) {
      toast.error("Capture or enter location");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        application_id: app.id,
        location: { lat: Number(form.location.lat), lng: Number(form.location.lng), accuracy_m: Number(form.location.accuracy_m) || null },
        address_observed: form.address_observed,
        checklist: { ...form.checklist, employees_count: Number(form.checklist.employees_count) || 0 },
        notes: form.notes,
        recommendation: form.recommendation,
      };
      await api.post("/field-verification", payload);
      toast.success("Field verification submitted");
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader title="Field Verification" subtitle="Geo capture, business checklist, recommendation." />

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2 space-y-4">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><MapPin className="h-4 w-4 text-accent" />Location &amp; Address</h3>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Latitude"><Input value={form.location.lat} onChange={(e) => setL("lat", e.target.value)} data-testid="fv-lat-input" /></Field>
            <Field label="Longitude"><Input value={form.location.lng} onChange={(e) => setL("lng", e.target.value)} data-testid="fv-lng-input" /></Field>
            <Field label="Accuracy (m)"><Input value={form.location.accuracy_m} onChange={(e) => setL("accuracy_m", e.target.value)} /></Field>
          </div>
          <Button type="button" variant="secondary" onClick={captureLocation} disabled={capturing} data-testid="fv-capture-button">{capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MapPin className="h-4 w-4 mr-1" />Capture GPS</>}</Button>
          <Field label="Address Observed"><Textarea rows={2} value={form.address_observed} onChange={(e) => set("address_observed", e.target.value)} /></Field>
          <div className="pt-3 border-t">
            <h3 className="text-sm font-semibold heading mt-2 mb-2">Business Checklist</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ChecklistSwitch label="Business Active" checked={form.checklist.business_active} onChange={(v) => setC("business_active", v)} />
              <ChecklistSwitch label="Signboard Visible" checked={form.checklist.signboard_visible} onChange={(v) => setC("signboard_visible", v)} />
              <ChecklistSwitch label="Stock Observed" checked={form.checklist.stock_observed} onChange={(v) => setC("stock_observed", v)} />
              <ChecklistSwitch label="Machinery Observed" checked={form.checklist.machinery_observed} onChange={(v) => setC("machinery_observed", v)} />
              <ChecklistSwitch label="Activity Matches Records" checked={form.checklist.activity_matches} onChange={(v) => setC("activity_matches", v)} />
              <Field label="Employees Count"><Input type="number" value={form.checklist.employees_count} onChange={(e) => setC("employees_count", e.target.value)} /></Field>
              <Field label="Market Reputation">
                <Select value={form.checklist.market_reputation} onValueChange={(v) => setC("market_reputation", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Good", "Average", "Poor"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Neighbor Feedback"><Input value={form.checklist.neighbor_feedback} onChange={(e) => setC("neighbor_feedback", e.target.value)} /></Field>
            </div>
          </div>
        </Card>
        <Card className="p-4 space-y-3 h-fit">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><Camera className="h-4 w-4 text-accent" />Notes &amp; Recommendation</h3>
          <Field label="Notes"><Textarea rows={6} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Site layout, observations, supporting evidence…" /></Field>
          <Field label="Recommendation">
            <Select value={form.recommendation} onValueChange={(v) => set("recommendation", v)}>
              <SelectTrigger data-testid="fv-recommendation-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Recommended", "Not Recommended", "Re-visit"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Button type="submit" disabled={busy} className="w-full" data-testid="fv-submit-button">{busy ? "Submitting…" : "Submit FV Report"}</Button>
          <div className="pt-2 border-t border-border space-y-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Site Photos & Videos</div>
            <ContextualUploader applicationId={app.id} category="PHOTO" title="Premises photos" accept="image/*,video/*" multiple compact />
          </div>
        </Card>
      </form>

      <Card className="p-4">
        <SectionHeader title="Previous Visits" />
        {(fvList || []).length === 0 && <div className="text-sm text-muted-foreground">No visits recorded yet.</div>}
        <ul className="divide-y divide-border">
          {fvList.map((fv) => (
            <li key={fv.id} className="py-3 flex flex-wrap items-center gap-3">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <div className="flex-1">
                <div className="font-medium text-sm">{fv.recommendation} · by {fv.officer_name}</div>
                <div className="text-xs text-muted-foreground mono">{fmtDate(fv.created_at)} · GPS {fv.location?.lat}, {fv.location?.lng} · ±{fv.location?.accuracy_m || "?"}m</div>
                {fv.notes && <div className="text-xs mt-1 text-muted-foreground">{fv.notes}</div>}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ChecklistSwitch({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between rounded-md border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
